import { buildPackageUrl } from './evaluationHelpers';

// arn:aws:sts::ACCOUNT:assumed-role/ROLE/SESSION
const ASSUMED_ROLE = /^arn:aws:sts::[^:]*:assumed-role\/([^/]+)\/(.+)$/;

/**
 * Extract a human-readable name from an AWS ARN.
 *
 * For STS assumed-role ARNs like:
 *   arn:aws:sts::123456789012:assumed-role/RoleName/user@amazon.com
 * Returns: "RoleName / user@amazon.com"
 *
 * For other ARN formats, returns the resource portion after the last colon or slash.
 * For non-ARN strings, returns the input unchanged.
 */
export function humanizeArn(arn: string): string {
  if (!arn || !arn.startsWith('arn:')) {
    return arn || '';
  }

  const assumedRoleMatch = arn.match(ASSUMED_ROLE);
  if (assumedRoleMatch) {
    const [, roleName, sessionName] = assumedRoleMatch;
    return `${roleName} / ${sessionName}`;
  }

  // Generic ARN: return the resource portion (after last colon or slash)
  const parts = arn.split(':');
  const resource = parts[parts.length - 1];
  const segments = resource.split('/');
  return segments[segments.length - 1] || arn;
}

/**
 * Check if a string looks like an AWS ARN.
 */
export function isArn(value: string): boolean {
  return !!value && value.startsWith('arn:');
}

type PrincipalClass = 'human' | 'registry' | 'automation';

/**
 * Who wrote a rule, from createdBy / lastModifiedBy: people (bare alias, `alias-Isengard` session, or a
 * session of this console's Midway role `consoleRoleArn`), the rules-registry reconciler, or other automation.
 */
export function classifyPrincipal(arn: string | undefined, consoleRoleArn?: string): PrincipalClass {
  if (!arn || !isArn(arn)) return 'human';
  const assumed = arn.match(ASSUMED_ROLE);
  if (!assumed) return 'automation';
  const [, roleName, sessionName] = assumed;
  if (/RulesReconciler/i.test(roleName)) return 'registry';
  if (sessionName.endsWith('-Isengard')) return 'human';
  if (consoleRoleArn && roleName === humanizeArn(consoleRoleArn)) return 'human';
  return 'automation';
}

/** The package that owns every deployed rule definition. */
export const RULES_REGISTRY_URL = buildPackageUrl('HelianthusArbiterRulesRegistry');

const REGISTRY_BLOBS = `${RULES_REGISTRY_URL}/blobs/mainline/--`;

/** Source-of-truth JSON for a registry-managed rule. Filename is the rule name by convention. */
export function registryRuleUrl(ruleName: string): string {
  return `${REGISTRY_BLOBS}/src/helianthus_arbiter_rules_registry/rules/${encodeURIComponent(ruleName)}.json`;
}

export const RULE_AUTHORING_URL = `${REGISTRY_BLOBS}/RULE_AUTHORING.md`;
